<template>
  <section>
    <div
      class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="text-start">
        <h2 class="text-xl font-bold text-gray-800">Layouts de relatório</h2>

        <p class="mt-1 text-sm text-gray-500">
          Gerencie os modelos visuais usados na emissão dos relatórios técnicos.
        </p>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ReportSearchBar
          :model-value="search"
          placeholder="Buscar por layout..."
          @update:model-value="setSearch"
        />

        <div
          class="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1"
        >
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="rounded-lg px-3 py-2 text-sm transition whitespace-nowrap"
            :class="
              statusFilter === option.value
                ? 'bg-white text-[#2f8f2f] shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            "
            @click="setStatus(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <ReportViewModeToggle
          :model-value="viewMode"
          @update:model-value="setViewMode"
        />

        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
          @click="openCreateDrawer"
        >
          <PhPlus class="h-5 w-5" />
          Novo layout
        </button>
      </div>
    </div>

    <ReportLayoutTable
      v-if="viewMode === 'table'"
      :layouts="filteredLayouts"
      @configure="configureLayout"
      @edit="openEditDrawer"
      @disable="disableLayout"
      @enable="enableLayout"
      @set-default="setDefaultLayout"
    />

    <ReportLayoutCards
      v-else
      :layouts="filteredLayouts"
      @configure="configureLayout"
      @edit="openEditDrawer"
      @disable="disableLayout"
      @enable="enableLayout"
      @set-default="setDefaultLayout"
    />

    <ReportLayoutFormDrawer
      v-model="isDrawerOpen"
      :layout="selectedLayout"
      :loading="isSaving"
      @submit="saveLayout"
      @close="clearSelectedLayout"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { PhPlus } from "@phosphor-icons/vue";
import { showToast as toast } from "@/helpers/UseToasts";

import { dataReport } from "../../store";
import ReportLayoutsService from "../../services/reportLayouts.service";

import ReportSearchBar from "../shared/ReportSearchBar.vue";
import ReportViewModeToggle from "../shared/ReportViewModeToggle.vue";
import ReportLayoutTable from "../layouts/ReportLayoutTable.vue";
import ReportLayoutCards from "../layouts/ReportLayoutCards.vue";
import ReportLayoutFormDrawer from "../layouts/ReportLayoutFormDrawer.vue";
import { useRouter } from "vue-router";

const isDrawerOpen = ref(false);
const isSaving = ref(false);
const selectedLayout = ref(null);
const router = useRouter();

const statusOptions = [
  {
    label: "Todos",
    value: "all",
  },
  {
    label: "Ativos",
    value: "active",
  },
  {
    label: "Inativos",
    value: "inactive",
  },
];

const search = computed(() => dataReport.state.filters.layoutsSearch);
const statusFilter = computed(
  () => dataReport.state.filters.layoutsStatus || "all"
);
const viewMode = computed(() => dataReport.state.layoutsViewMode || "table");
const layouts = computed(() => dataReport.state.layouts);

const filteredLayouts = computed(() => {
  const term = search.value.toLowerCase().trim();

  return layouts.value.filter((layout) => {
    const matchesStatus =
      statusFilter.value === "all" ||
      (statusFilter.value === "active" && layout.is_active) ||
      (statusFilter.value === "inactive" && !layout.is_active);

    if (!matchesStatus) return false;

    if (!term) return true;

    return [
      layout.name,
      layout.description,
      layout.page_format,
      layout.orientation,
    ]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });
});

onMounted(() => {
  loadLayouts();
});

async function loadLayouts() {
  dataReport.commit("setLayoutsLoading", true);

  try {
    const response = await ReportLayoutsService.getAll({
      status: "all",
    });

    const responseData =
      response?.data?.data || response?.data?.layouts || response?.data || [];

    dataReport.commit("setLayouts", responseData);
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível carregar os layouts.", "error");
  } finally {
    dataReport.commit("setLayoutsLoading", false);
  }
}

function setSearch(value) {
  dataReport.commit("setLayoutsSearch", value);
}

function setStatus(value) {
  dataReport.commit("setLayoutsStatus", value);
}

function setViewMode(mode) {
  dataReport.commit("setLayoutsViewMode", mode);
}

function openCreateDrawer() {
  selectedLayout.value = null;
  dataReport.commit("setSelectedLayout", null);
  isDrawerOpen.value = true;
}

function openEditDrawer(layout) {
  selectedLayout.value = { ...layout };
  dataReport.commit("setSelectedLayout", layout);
  isDrawerOpen.value = true;
}

function clearSelectedLayout() {
  selectedLayout.value = null;
  dataReport.commit("setSelectedLayout", null);
}

async function saveLayout({ id, payload, isEdit, assetFiles, assetPreviews }) {
  isSaving.value = true;

  try {
    const finalPayload = await uploadLayoutAssets({
      payload,
      isEdit,
      assetFiles,
      assetPreviews,
    });

    if (isEdit) {
      const response = await ReportLayoutsService.update(id, finalPayload);
      const updatedLayout = response?.data?.data || response?.data;

      handleLayoutMutation(updatedLayout);

      toast("Protege informa:", "Layout atualizado com sucesso.", "success");
    } else {
      const response = await ReportLayoutsService.create(finalPayload);
      const createdLayout = response?.data?.data || response?.data;

      handleLayoutMutation(createdLayout, true);

      toast("Protege informa:", "Layout cadastrado com sucesso.", "success");
    }

    isDrawerOpen.value = false;
    clearSelectedLayout();
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      getFirstValidationError(error) ||
        "Não foi possível salvar o layout. Verifique os dados e tente novamente.",
      "error"
    );
  } finally {
    isSaving.value = false;
  }
}

async function uploadLayoutAssets({
  payload,
  isEdit,
  assetFiles = {},
  assetPreviews = {},
}) {
  const finalPayload = { ...payload };

  const assetTypes = ["cover", "header", "footer", "watermark"];

  for (const type of assetTypes) {
    const file = assetFiles[type];

    if (!file) continue;

    const currentPath = payload[`${type}_path`];

    const response =
      isEdit && currentPath
        ? await ReportLayoutsService.replaceAsset(file, currentPath)
        : await ReportLayoutsService.saveAsset(file);

    const uploadedFile = extractUploadedFile(response);

    finalPayload[`${type}_path`] =
      uploadedFile.path || finalPayload[`${type}_path`] || "";

    finalPayload[`${type}_url`] =
      uploadedFile.url ||
      finalPayload[`${type}_url`] ||
      assetPreviews[type] ||
      "";
  }

  return finalPayload;
}

function extractUploadedFile(response) {
  const data = response?.data || response?.data?.data || {};

  return {
    path:
      data ||
      data.path ||
      data.file_path ||
      data.filePath ||
      data.token ||
      data.name ||
      "",

    url: process.env.VUE_APP_IMAGE_SERVE.concat('storage/').concat(
      data ||
      data.url ||
      data.file_url ||
      data.fileUrl ||
      data.full_url ||
      data.public_url ||
      ""),
  };
}

async function disableLayout(layout) {
  const confirmDisable = window.confirm(
    `Deseja realmente inativar o layout "${layout.name}"?`
  );

  if (!confirmDisable) return;

  try {
    const response = await ReportLayoutsService.disable(layout.id);
    const updatedLayout = response?.data?.data || response?.data;

    handleLayoutMutation(updatedLayout);

    toast("Protege informa:", "Layout inativado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível inativar o layout.", "error");
  }
}

async function enableLayout(layout) {
  const confirmEnable = window.confirm(
    `Deseja realmente reativar o layout "${layout.name}"?`
  );

  if (!confirmEnable) return;

  try {
    const response = await ReportLayoutsService.enable(layout.id);
    const updatedLayout = response?.data?.data || response?.data;

    handleLayoutMutation(updatedLayout);

    toast("Protege informa:", "Layout reativado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível reativar o layout.", "error");
  }
}

async function setDefaultLayout(layout) {
  const confirmDefault = window.confirm(
    `Deseja definir "${layout.name}" como layout padrão?`
  );

  if (!confirmDefault) return;

  try {
    const response = await ReportLayoutsService.setDefault(layout.id);
    const updatedLayout = response?.data?.data || response?.data;

    await loadLayouts();

    if (updatedLayout) {
      dataReport.commit("updateLayout", updatedLayout);
    }

    toast(
      "Protege informa:",
      "Layout definido como padrão com sucesso.",
      "success"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível definir o layout como padrão.",
      "error"
    );
  }
}

function handleLayoutMutation(layout, shouldAdd = false) {
  if (!layout) return;

  if (layout.is_default) {
    const normalizedLayouts = layouts.value.map((currentLayout) => ({
      ...currentLayout,
      is_default:
        Number(currentLayout.id) === Number(layout.id)
          ? layout.is_default
          : false,
    }));

    dataReport.commit("setLayouts", normalizedLayouts);
  }

  if (shouldAdd) {
    dataReport.commit("addLayout", layout);
    return;
  }

  dataReport.commit("updateLayout", layout);
}

function getFirstValidationError(error) {
  const errors = error?.response?.data?.errors;

  if (!errors) return null;

  const firstKey = Object.keys(errors)[0];

  if (!firstKey) return null;

  return errors[firstKey]?.[0] || null;
}

function configureLayout(layout) {
  router.push({
    name: "reports-layout-editor",
    params: {
      id: layout.id,
    },
  });
}
</script>
